/**
 * MatrixBackground - Basketball-themed Matrix animation overlay
 * Used during video analysis to create an immersive "SLAM AI" aesthetic
 */

import React, { useEffect, useRef } from 'react';

const MatrixBackground = ({ isActive, phase = 'analyzing' }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Basketball-themed Matrix characters - SLAM BALL AI themed
    const chars = [
      '0', '1', '0', '1', '0', '1', '0', '1', '0', '1',
      // Basketball stats
      '3PT', 'FT', 'MR', 'PU', 'FD', 'SB', 'CS', 'DK', 'FL', 'CR',
      // Angles and metrics
      '45°', '48°', '52°', 'ARC', '0.4s', '0.5s', '10ft', '23ft', '24ft', '26ft',
      // Advanced stats
      'PTS', 'AST', 'REB', 'BLK', 'STL', 'FG%', 'TS%', 'eFG', 'PER', 'USG', 'ORTG', 'DRTG', 'VORP',
      // Biomechanics
      'FORM', 'SHOT', 'ARC', 'SET', 'WRIST', 'FOOT', 'BASE', 'LIFT',
      'SPIN', 'DROP', 'ENTRY', 'ANGLE', 'RELEASE', 'FOLLOW', 'HOLD',
      // Body parts
      'HEAD', 'ELBOW', 'WRIST', 'HIP', 'KNEE', 'FOOT', 'TRUNK', 'TORSO',
      // Analysis terms
      'SCAN', 'DETECT', 'ALERT', 'FOUND', 'MATCH', 'PATTERN', 'SIGNAL', 'LOCK',
      // Branding
      'SLAM', 'AI', 'BALL', 'DATA', 'STAT', 'WIRE',
      // Positions
      'PG', 'SG', 'SF', 'PF', 'C', 'NBA', 'NCAA', 'FIBA', 'ACB',
      // Emojis
      '🏀', '🏟️', '👟', '🏆', '⭐', '🔥', '💪', '👀', '🎯', '📊', '🖐️',
      // Arrows
      '⬆️', '⬇️', '↗️', '↘️', '↖️', '↙️', '➡️', '⬅️', '💨', '✨', '⚡',
      // Shapes
      '●', '◯', '◆', '▲', '▼', '◀', '▶', '■', '□', '◉', '◎',
      // Jersey numbers
      '#0', '#3', '#11', '#23', '#24', '#30', '#35',
      // Callouts
      'GO', 'YES', 'SWISH', 'BRICK', 'AIR', 'BALL', 'SCORE',
      // Metrics
      'RPM', 'FPS', 'DEG', 'PPP', 'QSQ', 'xEFG', 'COD', 'ACC',
      // Teams (El Clasico specific)
      'RMA', 'BAR', 'P&R', 'ISO', 'DHO', 'ICE', 'DROP',
    ];

    const fontSize = 12;
    const columns = Math.floor(window.innerWidth / (fontSize * 0.8));

    const drops = [];
    const speeds = [];
    const charIndices = [];
    const brightness = [];

    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -30;
      speeds[i] = 0.03 + Math.random() * 0.07;
      charIndices[i] = Math.floor(Math.random() * chars.length);
      brightness[i] = 0.1 + Math.random() * 0.15;
    }

    const draw = () => {
      ctx.fillStyle = 'rgba(10, 10, 10, 0.08)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);

      ctx.font = `${fontSize}px 'Courier New', monospace`;

      for (let i = 0; i < drops.length; i++) {
        if (Math.random() > 0.97) {
          charIndices[i] = Math.floor(Math.random() * chars.length);
        }

        const char = chars[charIndices[i]];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        const progress = y / window.innerHeight;
        let alpha = brightness[i];

        if (progress < 0.2) {
          alpha *= 0.9;
        } else if (progress > 0.7) {
          alpha *= 0.3;
        }

        // SLAM Gold color (#FFD700) with muted alpha
        ctx.fillStyle = `rgba(255, 215, 0, ${alpha * 0.25})`;

        // Rare subtle glow
        if (Math.random() > 0.998) {
          ctx.shadowColor = '#FFD700';
          ctx.shadowBlur = 8;
          ctx.fillStyle = `rgba(255, 215, 0, ${alpha * 0.4})`;
        } else {
          ctx.shadowBlur = 0;
        }

        ctx.fillText(char, x, y);

        if (y > window.innerHeight && Math.random() > 0.95) {
          drops[i] = Math.random() * -10;
          speeds[i] = 0.03 + Math.random() * 0.07;
          brightness[i] = 0.1 + Math.random() * 0.15;
        }

        drops[i] += speeds[i];
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ imageRendering: 'pixelated' }}
      />

      {/* Vignette overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(0,0,0,0.6) 100%)'
        }}
      />

      {/* Scan lines effect - gold tinted */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,215,0,0.1) 2px, rgba(255,215,0,0.1) 4px)',
        }}
      />

      {/* Phase indicator */}
      {phase && (
        <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur rounded-full">
          <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
          <span className="text-yellow-400 text-xs font-mono uppercase tracking-wider">
            {phase === 'analyzing' && 'ANALYZING VIDEO...'}
            {phase === 'processing' && 'PROCESSING DATA...'}
            {phase === 'uploading' && 'UPLOADING...'}
          </span>
        </div>
      )}
    </div>
  );
};

export default MatrixBackground;
